/* ====================================================================
   Homepage reviews - product-led tiles.

   Judge.me owns this markup. It arrives as
   `shop.metafields.judgeme.featured_carousel` (a shell) and their
   loader.js fills in the items client-side, so everything below has to
   work on a DOM that appears LATER and may be rebuilt at any time.

   Four things the CSS cannot do on its own:

   1. THE IMAGE IS 70px. Judge.me requests `..._70x70.png` with a 140px
      retina twin. This design makes the product image the whole tile, so
      70px would be a blurred mess. The source is a Shopify CDN URL with
      the size in the filename, so asking for a bigger one is a string
      replace - there is no new request to anyone, just a larger variant
      of the same asset.

   2. THE IMAGE NEVER LOADS. Judge.me ships it as `data-src` with an
      empty `src` and swaps them in its own lazy-loader. On this page
      that swap does not happen - `src` stays empty, the browser renders
      the alt text, and the tile looks broken. See setSrc below.

   3. THERE IS NO PRODUCT NAME ELEMENT. The product title exists only as
      the image's `alt`. To show it under the tile it has to be lifted
      into a real element.

   4. THERE IS NO PRODUCT LINK. Judge.me does not include one, and the
      image filename is not a reliable handle. The tile therefore links
      to a search for the exact product title, which lands on the product
      in practice - see LINKS below for why this is a compromise.

   Everything here is idempotent: Judge.me re-renders on resize and on
   carousel paging, so each pass re-checks a flag before touching a tile.
   ==================================================================== */

(function () {
  'use strict';

  var wrap = document.querySelector('.qn-home-reviews');
  if (!wrap) return;

  var DONE = 'data-qn-done';

  /* Used only for the wordless reviews - see fillBody. Index is the star
     count, so the 0 slot is a placeholder and never read. */
  var STARS = ['', 'One star', 'Two stars', 'Three stars', 'Four stars', 'Five stars'];

  /*
    Ask the Shopify CDN for a bigger variant of the same file. The size is
    a filename suffix (`_70x70.png`), so this is a rename, not a new
    asset - and if the pattern ever stops matching, the original 70px URL
    is left exactly as it was rather than being mangled.
  */
  function upsize(url) {
    if (!url) return url;
    return url.replace(/_(\d+)x(\d+)(\.[a-z]+)/i, '_640x640$3');
  }

  /*
    Judge.me lazy-loads by copying data-src into src on its own schedule,
    and on this page that never happens: src stays empty and the browser
    falls back to drawing the alt text, which is what a "broken" tile is.
    Setting src here is the only reliable way to get a picture.

    loading="lazy" hands the deferral to the browser instead, so the
    off-screen tiles - this block sits well below the fold - still stay
    off the critical path and the homepage LCP is unaffected.
  */
  function setSrc(img) {
    ['data-src', 'data-src-retina', 'src'].forEach(function (attr) {
      var v = img.getAttribute(attr);
      if (v) img.setAttribute(attr, upsize(v));
    });

    if (img.getAttribute('src')) return;    /* Judge.me got there first */
    var want = img.getAttribute('data-src');
    if (!want) return;                      /* nothing to show - handled by the caller */
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    img.setAttribute('src', want);
  }

  /*
    A five-star review with no words renders as a product name and then
    nothing at all. Say what the rating was instead. The star count is
    only exposed on the rating element's aria-label ("5 stars").

    The review TITLE is deliberately not used as a fallback: Judge.me
    fills it with the product name on some reviews, which would quote the
    customer as having said the name of the thing they bought.
  */
  function fillBody(item) {
    var body = item.querySelector('.jdgm-carousel-item__review-body');
    if (!body || body.textContent.trim()) return;

    var rating = item.querySelector('.jdgm-carousel-item__review-rating');
    var n = rating ? parseInt(rating.getAttribute('aria-label'), 10) : 0;
    if (!STARS[n]) return;                  /* no rating either - leave it alone */

    /* A <p>, because the quote marks are drawn on the paragraph. */
    var p = document.createElement('p');
    p.textContent = STARS[n] + '.';
    body.appendChild(p);
  }

  function enhance(item) {
    if (item.getAttribute(DONE)) return;

    var img = item.querySelector('.jdgm-carousel-item__product-image');
    if (!img) return;            /* not filled in yet - a later pass gets it */

    item.setAttribute(DONE, '1');
    setSrc(img);
    fillBody(item);

    /* Reviews left against no product arrive with an empty image and no
       alt. The tile becomes a text-only quote card rather than a blank
       square, and gets no name and no link because there is nothing to
       name or link to. */
    if (!img.getAttribute('src')) item.classList.add('qn-rev--noimg');

    var title = (img.getAttribute('alt') || '').trim();
    if (!title) return;

    /*
      LINKS. Judge.me gives no product URL, so this searches for the exact
      title. Shopify puts an exact title match first, so it lands on the
      product - but it IS a search page, not the product page, and a
      renamed product would land on results rather than nowhere. If
      Judge.me ever exposes a product handle, swap this for a real URL.
    */
    var link = document.createElement('a');
    link.className = 'qn-rev__link';
    link.href = '/search?q=' + encodeURIComponent(title);
    link.setAttribute('aria-label', 'See ' + title);

    var name = document.createElement('span');
    name.className = 'qn-rev__product';
    name.textContent = title;

    /* The name goes above the review body, under the image, which is
       where the design puts it - Judge.me has no slot for it at all. */
    var body = item.querySelector('.jdgm-carousel-item__review-wrapper');
    if (body) body.insertBefore(name, body.firstChild);

    item.appendChild(link);      /* stretched over the tile by CSS */
  }

  function sweep() {
    wrap.querySelectorAll('.jdgm-carousel-item').forEach(enhance);
  }

  /*
    Judge.me renders after its loader arrives, then re-renders on resize
    and when the carousel pages. An observer is the only honest way to
    catch all of that - polling would either miss it or never stop.
  */
  if (window.MutationObserver) {
    new MutationObserver(sweep).observe(wrap, { childList: true, subtree: true });
  }

  sweep();
  /* A couple of cheap late passes, for the case where the observer is
     unavailable and the widget lands after first paint. */
  setTimeout(sweep, 1200);
  setTimeout(sweep, 4000);
})();
